import { USE_BACKEND_API } from '../config/features';
import clientImagesData from '../data/clientImagesData.json';

export interface GalleryImage {
  id: number;
  url: string;
  alt: string;
  caption?: string;
}

interface ClientImagesData {
  [clientId: string]: {
    clientId: number;
    clientName: string;
    images: GalleryImage[];
  };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Sanitize client name to match server-side sanitization
const sanitizeClientName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Fetch images from the backend API
 */
const fetchFromBackend = async (clientId: number, clientName: string): Promise<GalleryImage[]> => {
  try {
    const response = await fetch(
      `${API_URL}/api/images/${clientId}?name=${encodeURIComponent(clientName)}`
    );

    if (!response.ok) {
      console.error('Failed to fetch images from backend:', response.statusText);
      return [];
    }

    const data = await response.json();
    return data.images || [];
  } catch (error) {
    console.error('Error fetching images from backend:', error);
    return [];
  }
};

/**
 * Fetch images from static JSON file
 */
const fetchFromJSON = async (clientId: number, clientName: string): Promise<GalleryImage[]> => {
  try {
    const typedData = clientImagesData as ClientImagesData;
    const clientData = typedData[clientId.toString()];

    if (!clientData || !clientData.images) {
      return [];
    }

    return clientData.images;
  } catch (error) {
    console.error('Error fetching images from JSON:', error);
    return [];
  }
};

/**
 * Main service to fetch client images
 * Uses feature flag to determine source (backend API or static JSON)
 */
export const fetchClientImages = async (
  clientId: number,
  clientName: string
): Promise<GalleryImage[]> => {
  if (USE_BACKEND_API) {
    console.log(`[ImageService] Fetching from backend API for client: ${clientName}`);
    return fetchFromBackend(clientId, clientName);
  } else {
    console.log(`[ImageService] Fetching from static JSON for client: ${clientName}`);
    return fetchFromJSON(clientId, clientName);
  }
};

export { sanitizeClientName };
